/** @format */
"use client";
import { Text } from "@radix-ui/themes";
import React, { useState, useEffect } from "react";
import EmailLogin, { EmailCredential } from "./emailLogin";
import PhoneNumberLogin, { PhoneNUmberCredential } from "./phoneNumberLogin";
import {
  getMasterConfig,
  getProfile,
  requestOTP,
  requestToChangePassword,
  signIn,
  verifyOTP,
} from "../redux/reducers/login";
import { useRouter } from "next/navigation";
import {
  removeDataFromSessionStorage,
  saveDataToSessionStorage,
} from "../services/storageService";
import { useDispatch } from "react-redux";
import { saveAuthUser } from "../redux/reducers/authReducer";
import ResetPassword, { ResetProps } from "./resetPassword";
import { extractMessage } from "../services/utilService";
import { showMessage } from "../redux/reducers/toastReducer";
import {
  ScreenView,
  ScreenViewButtonLabel,
  ScreenViewHeading,
  ScreenViewSubHeading,
} from "./loginUtility";
import { MixPanelService } from "../services/mixpanelService";
import LoginLayout from "../common/loginLayout/loginLayout";

const getEmailFromStorage = (): string | null => {
  if (typeof window !== "undefined") {
    return localStorage.getItem("login_email");
  }
  return null;
};

const clearEmailFromStorage = () => {
  if (typeof window !== "undefined") {
    localStorage.removeItem("login_email");
  }
};

const Login: React.FC = () => {
  const [currentScreenView, setCurrentScreenView] = useState<string>(
    ScreenView.EMAIL
  );

  const [loginOptions, setLoginOptions] = useState<string[]>([]);
  const [enableResendCounter, setResendCounter] = useState<boolean>(false);
  const [session, setSession] = useState<any>(null);
  const [isLoading, setLoader] = useState<boolean>(true);
  const [isSubmitted, setSubmitStatus] = useState<boolean>(false);
  const [credential, setCredential] = useState<
    (EmailCredential & PhoneNUmberCredential & ResetProps) | null
  >(null);
  const dispatch = useDispatch();
  const router = useRouter();

  useEffect(() => {
    const persistedEmail = getEmailFromStorage();

    if (persistedEmail) {
      setCredential({
        email: persistedEmail,
        isValidEmail: true,
      } as EmailCredential & PhoneNUmberCredential & ResetProps);
    }
    clearEmailFromStorage();
  }, []);

  const handleBack = () => {
    setSubmitStatus(false);
    setCurrentScreenView(
      currentScreenView === ScreenView.OTP
        ? ScreenView.PHONE_NUMBER
        : ScreenView.EMAIL
    );
    if (credential && credential.password)
      setCredential({ ...credential, password: "" });
  };

  const handleEmailLogin = () => {
    setLoader(true);
    MixPanelService.track("user_login", { context: "email" });

    signIn(credential)
      .then((loginResponse) => {
        if (loginResponse.data.challengeName) {
          setSession(loginResponse.data.session);
          if (credential && credential.password)
            setCredential({ ...credential, password: "" });
          setCurrentScreenView(ScreenView.RESET_PASSWORD);
          setLoader(false);
          setSubmitStatus(false);
        } else {
          getProfile().then((response) => {
            if (response) {
              saveDataToSessionStorage("user-role", response.data.role);
              dispatch(saveAuthUser(response.data));
              setLoader(false);
            }
          });
        }
      })
      .catch((error: any) => {
        dispatch(
          showMessage({ title: "Login", description: extractMessage(error) })
        );
        setLoader(false);
      });
  };

  const isActionDisabled = (): boolean => {
    if (currentScreenView === ScreenView.EMAIL && !credential?.isValidEmail)
      return true;
    if (
      currentScreenView === ScreenView.PASSWORD &&
      !credential?.isValidPassword
    )
      return true;
    if (
      currentScreenView === ScreenView.PHONE_NUMBER &&
      !credential?.isValidPhoneNumber
    )
      return true;
    if (currentScreenView === ScreenView.OTP && !credential?.isValidOTP)
      return true;
    if (
      currentScreenView === ScreenView.RESET_PASSWORD &&
      (credential?.password?.length === 0 ||
        credential?.confirmPassword?.length === 0)
    )
      return true;

    return false;
  };

  const handlePhoneNumberLogin = async (isResend: boolean = false) => {
    setLoader(true);
    if (currentScreenView === ScreenView.PHONE_NUMBER || isResend)
      if (!isResend) MixPanelService.track("request_otp");
      else MixPanelService.track("resend_otp");
    requestOTP({
      countryCode: credential?.countryCode,
      phone: credential?.phone,
    })
      .then((response) => {
        setSession(response.data.session);
        setCurrentScreenView(ScreenView.OTP);
        setResendCounter(true);
        setSubmitStatus(false);
        setLoader(false);
      })
      .catch((error: any) => {
        dispatch(
          showMessage({ title: "Login", description: extractMessage(error) })
        );
        setLoader(false);
      });

    if (currentScreenView === ScreenView.OTP && !isResend)
      verifyOTP({
        credential: {
          countryCode: credential?.countryCode,
          phone: credential?.phone,
        },
        session: session,
        otp: credential?.otp,
      })
        .then(async (response) => {
          if (response.data.challengeName) {
            setSession(response.data.session);
            dispatch(
              showMessage({
                title: "Login",
                description: "Your OTP is either invalid/expired",
              })
            );
          } else {
            MixPanelService.track("user_login", { context: "phone_number" });
            getProfile().then((response) => {
              if (response) {
                saveDataToSessionStorage("user-role", response.data.role);
                dispatch(saveAuthUser(response.data));
              }
              setLoader(false);
            });
          }
          setSubmitStatus(false);
          setLoader(false);
        })
        .catch((error) => {
          dispatch(
            showMessage({
              title: "Login",
              description: extractMessage(error),
            })
          );
          setLoader(false);
        });
  };

  const handleResetPassword = () => {
    if (!credential?.isValidConfirmPassword) return;
    setLoader(true);

    removeDataFromSessionStorage("login_email");

    requestToChangePassword({
      email: credential?.email,
      password: credential?.password,
      session: session,
    })
      .then((linkResponse: any) => {
        if (linkResponse) {
          setLoader(false);
          dispatch(
            showMessage({
              title: "Set New Password",
              description: "The password has been changed successfully",
            })
          );

          setSubmitStatus(false);
          setCredential(null);
          setCurrentScreenView(ScreenView.EMAIL);
        }
      })
      .catch((error: any) => {
        dispatch(
          showMessage({
            title: "Set New Password",
            description: extractMessage(error),
          })
        );
        setLoader(false);
      });
  };

  const handleContinueCallback = () => {
    setSubmitStatus(true);

    if (currentScreenView === ScreenView.RESET_PASSWORD) {
      handleResetPassword();
      return;
    }
    if (
      currentScreenView === ScreenView.EMAIL &&
      credential &&
      credential.isValidEmail
    ) {
      setCurrentScreenView(ScreenView.PASSWORD);
      setSubmitStatus(false);
      return;
    }
    if (
      credential &&
      credential.isValidPassword &&
      currentScreenView === ScreenView.PASSWORD
    ) {
      handleEmailLogin();
      return;
    }

    if (
      currentScreenView === ScreenView.PHONE_NUMBER &&
      credential &&
      credential.isValidPhoneNumber
    ) {
      handlePhoneNumberLogin();
      return;
    }

    if (
      currentScreenView === ScreenView.OTP &&
      credential &&
      credential.isValidOTP
    ) {
      handlePhoneNumberLogin();
      return;
    }
  };

  const handleNavigateToForgotPassword = () => {
    if (credential?.email) {
      if (typeof window !== "undefined") {
        saveDataToSessionStorage("login_email", credential.email);
      }
    }

    setSubmitStatus(false);
    router.push("/forgot-password");
  };

  const getMasterConfigInfo = () => {
    setLoader(true);
    getMasterConfig().then((response) => {
      if (response && response.data) {
        saveDataToSessionStorage(
          "login-assets",
          JSON.stringify(response.data.socialMediaOptions)
        );
        setLoginOptions([...response.data.socialMediaOptions]);
      }
      setLoader(false);
    });
  };

  useEffect(() => {
    getMasterConfigInfo();
  }, []);

  return (
    <LoginLayout
      loginOptions={loginOptions}
      screenViewName="login_screen"
      handleBack={() => handleBack()}
      enableBackAction={[
        ScreenView.OTP,
        ScreenView.PASSWORD,
        ScreenView.RESET_PASSWORD,
      ].includes(currentScreenView)}
      screenViewHeading={ScreenViewHeading[currentScreenView] || ""}
      screenViewSubHeading={
        <>
          {ScreenViewSubHeading[currentScreenView] && (
            <Text
              as="div"
              size="2"
              mb="5"
              mt="1"
              weight="regular"
              color="gray"
              className=" text-center px-[10%]"
            >
              {currentScreenView === ScreenView.EMAIL &&
                !loginOptions.includes("phone") &&
                ScreenViewSubHeading["EMAIL_ONLY"]}

              {currentScreenView === ScreenView.EMAIL &&
                loginOptions.includes("phone") &&
                ScreenViewSubHeading[ScreenView.EMAIL]}

              {currentScreenView === ScreenView.PHONE_NUMBER &&
                !loginOptions.includes("email") &&
                ScreenViewSubHeading["PHONE_NUMBER_ONLY"]}

              {currentScreenView === ScreenView.PHONE_NUMBER &&
                loginOptions.includes("email") &&
                ScreenViewSubHeading[ScreenView.PHONE_NUMBER]}

              {![ScreenView.EMAIL, ScreenView.PHONE_NUMBER].includes(
                currentScreenView
              ) && ScreenViewSubHeading[currentScreenView]}
            </Text>
          )}
          {[ScreenView.PASSWORD, ScreenView.OTP].includes(
            currentScreenView
          ) && (
            <Text
              as="div"
              size="2"
              mb="6"
              mt="2"
              weight="regular"
              color="gray"
              className=" text-center"
            >
              {currentScreenView === ScreenView.PASSWORD
                ? "Enter the password for the email address"
                : "Enter the one time password sent to"}
              <strong className="pl-1">
                {credential &&
                  (credential.email
                    ? credential.email
                    : credential.countryCode + " " + credential.phone)}
              </strong>
            </Text>
          )}
        </>
      }
      contentView={
        <>
          {currentScreenView === ScreenView.RESET_PASSWORD && (
            <ResetPassword
              handleBackToLogin={() => {
                setSubmitStatus(false);
                setCredential(null);
                setCurrentScreenView(ScreenView.EMAIL);
              }}
              isSubmitted={isSubmitted}
              credentialData={credential}
              handleCredentialData={(data: any) => setCredential(data)}
            />
          )}
          {[ScreenView.EMAIL, ScreenView.PASSWORD].includes(
            currentScreenView
          ) && (
            <EmailLogin
              loginOptions={loginOptions}
              enablePassword={currentScreenView === ScreenView.PASSWORD}
              isSubmitted={isSubmitted}
              onEnablePhoneNumberView={() => {
                setSubmitStatus(false);
                setCredential(null);

                setCurrentScreenView(ScreenView.PHONE_NUMBER);
              }}
              onEnableForgotPasswordView={() =>
                handleNavigateToForgotPassword()
              }
              credentialData={credential}
              handleCredentialData={(data: any) => setCredential(data)}
            />
          )}
          {[ScreenView.PHONE_NUMBER, ScreenView.OTP].includes(
            currentScreenView
          ) && (
            <PhoneNumberLogin
              resendCounter={enableResendCounter && !isLoading}
              credentialData={credential}
              enableOTP={currentScreenView === ScreenView.OTP}
              isSubmitted={isSubmitted}
              handleCredentialData={(data: any) => setCredential(data)}
              onEnableEmailView={() => {
                setSubmitStatus(false);
                setCredential(null);
                setCurrentScreenView(ScreenView.EMAIL);
              }}
              onResendOTP={() => handlePhoneNumberLogin(true)}
            />
          )}
        </>
      }
      disableSubmitAction={isActionDisabled()}
      submitActionLabel={ScreenViewButtonLabel[currentScreenView]}
      hanldeSubmitAction={() => handleContinueCallback()}
      isLoading={isLoading}
    />
  );
};
export default Login;
